const {registerModule, KitModule} = window;

class HomepageHero extends KitModule {
  constructor($element) {
    super($element);

    this.setVars();
    if (!this.prefersReducedMotion) {
      this.bindUI();
      this.bindEvents();
      this.bindObservers();
    }
  }

  /**
   * @param {Number} timestamp
   */
  animate(timestamp) {
    this.animationTimestamp = this.animationTimestamp || 0;

    const refreshTimeout = this.isAnimating ? 33 : 16;
    if (timestamp - this.animationTimestamp < refreshTimeout) {
      // Only animate 30x/s when idle animation is running
      if (this.shouldAnimateAgain) {
        requestAnimationFrame(this.animate);
        return;
      }
    }
    this.animationTimestamp = timestamp;

    for (let col = 0; col < this.gridCols; col++) {
      for (let row = 0; row < this.gridRows; row++) {
        const coord = `${col},${row}`;
        const rectX = col * this.gridTileSize + this.pxRatio * col;
        const rectY = row * this.gridTileSize + this.pxRatio * row;
        const rectW = this.gridTileSize;
        const rectH = this.gridTileSize;

        this.ctx.clearRect(rectX, rectY, rectW, rectH);

        const tile = this.tiles[coord];
        if (tile) {
          const rgbColor = this.tileColors[coord];

          this.ctx.fillStyle = `rgba(${rgbColor}, ${this.tiles[coord].opacity})`;
          this.ctx.fillRect(rectX, rectY, rectW, rectH);

          // Automatically adjust opacity of tiles
          setTimeout( () => {
            const now = Date.now() - tile.delay;

            if (now - tile.created > tile.clearAfter) {
              // Fade out after the specified time
              tile.opacityDirection = -1;
            }

            tile.opacity += tile.opacityDirection * tile.opacityDelta;
            if (tile.opacity > tile.opacityMax) {
              tile.opacity = tile.opacityMax;
            } else if (tile.opacity <= 0) {
              // Once tile fades completely, remove it so it can be drawn again
              delete this.tiles[coord];
            }
          }, tile.delay);
        }
      }
    }

    if (this.shouldAnimateAgain) {
      requestAnimationFrame(this.animate);
    }
  }

  bindEvents() {
    // window.addEventListener('resize', this.handleWindowResize.bind(this));
    window.addEventListener('resize', this.isZooming.bind(this));
    window.addEventListener('devicepixelratiochange', this.handleWindowResize.bind(this));

    // Observing mouse events on the .section element and translating them to
    // tile rendering on the canvas (instead of observing the canvas directly)
    // allows us to preserve text selection.
    this.$content.addEventListener('mousemove', this.handleMouseMove.bind(this));
  }

  bindObservers() {
    const elementObserver = new IntersectionObserver(
      this.handleOnIntersect.bind(this), {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      },
    );
    elementObserver.observe(this.$element);
  }

  async bindUI() {
    // DOM elements to be included when calculating areas to avoid
    this.$avoidanceElements = [this.$title, this.$body];

    await this.updateCanvas();
    this.renderGradient();
    this.updateAvoidanceAreas();
  }

  /**
   * Get {col, row} from x- and y-pixel coordinates
   * @param {Number} x
   * @param {Number} y
   * @return {Object}
   */
  getCoordinates(x, y) {
    const posX = x * this.pxRatio;
    const posY = y * this.pxRatio;

    return {
      col: Math.floor(posX / (this.gridTileSize + this.pxRatio)),
      row: Math.floor(posY / (this.gridTileSize + this.pxRatio)),
    };
  }

  /**
   * Return the requested value from the current pre-computed Perlin set,
   * defined in $dataPerlin.
   *
   * @see _11ty/filters/perlin.js
   * @param {Number} col
   * @param {Number} row
   * @return {Number}
   */
  getPerlin(col, row) {
    const perlinSet = this.computedPerlinSets[this.currentPerlinSet];
    const index = row * perlinSet.cols + col;
    return perlinSet?.values[index] || 0;
  }

  /**
   * Specify that a grid tile should be made active beginning on the next
   * animation frame.
   *
   * @param {Number} col - grid column (ex., 24)
   * @param {Number} row - grid row (ex., 4)
   * @param {Object} [options]
   * @param {Number} [options.clearAfter=1500] - milliseconds after which to automatically clear tile
   * @param {Number} [options.delay=0] - milliseconds after which to begin drawin gtile
   * @param {Number} [options.opacityDelta=0.025] - rate at which to change opacity
   * @param {Number} [options.opacityMax=1] - maximum opacity
   * @param {boolean} [options.replace=false] - whether to replace an existing tile (ex., mouse events should replace idle animation tiles)
   * @param {string} [options.source] - ex., "pointer"
   */
  drawAt(col, row, {clearAfter=1500, delay=0, opacityDelta=0.0125, opacityMax=1, replace=false, source} = {}) {
    const coord = `${col},${row}`;
    if (!this.tiles[coord] || replace && source !== this.tiles[coord].source) {
      this.tiles[coord] = {
        clearAfter,
        created: Date.now(),
        delay,
        opacity: 0,
        opacityDelta,
        opacityDirection: 1,
        opacityMax,
        source,
      };
    }
  }

  /**
   * Stop idle animation and draw tiles to match mouse movement.
   *
   * @param {Event} event
   */
  handleMouseMove(event) {
    if (this.isAnimating) {
      this.stopIdleAnimation();
    } else {
      clearTimeout(this.animationTimeouts.mouseIdle);
    }

    const {currentTarget, pageX, pageY} = event;
    const x = currentTarget.offsetLeft + pageX;
    const y = currentTarget.offsetTop + pageY;

    const {col, row} = this.getCoordinates(x, y);

    this.drawAt(col, row, {
      clearAfter: 750,
      opacityDelta: 0.0625,
      opacityMax: 0.35 + Math.random() * 0.65,
      replace: true,
      source: 'pointer',
    });

    this.animationTimeouts.mouseIdle = setTimeout( () => {
      this.startIdleAnimation(0);
    }, this.IDLE_ANIMATION_TIMEOUT );
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  handleOnIntersect(entries) {
    this.isIntersectingViewport = entries[0]?.isIntersecting;
    if (this.isIntersectingViewport) {
      // If the element is visible in the viewport, animation should continue
      this.shouldAnimateAgain = true;
      this.animate();

      this.startIdleAnimation(750);
    } else {
      // If the element isn't visible in the viewport, animation should stop
      this.shouldAnimateAgain = false;
      this.stopIdleAnimation();
    }
  }

  /**
   *
   */
  async handleWindowResize() {
    this.isMobile = window.innerWidth <= this.BREAKPOINT_MOBILE;
    this.isTablet = window.innerWidth > this.BREAKPOINT_MOBILE && window.innerWidth < this.BREAKPOINT_DESKTOP;
    this.isDesktop = window.innerWidth >= this.BREAKPOINT_DESKTOP;

    // Take canvas out of DOM tree so it doesn't affect size of section
    this.$canvas.style.display = 'none';

    await this.updateCanvas();
    this.renderGradient();
    this.updateAvoidanceAreas();

    // Put the canvas back when we're done
    this.$canvas.style.display = 'block';
  }

  /**
   * Render the gradient on hidden <canvas> for sampling during animation
   */
  renderGradient() {
    const x1 = 0;
    const y1 = 0;
    const x2 = this.$gradientCanvas.width;
    const y2 = this.$gradientCanvas.height;

    const gradient = this.gradientCtx.createLinearGradient(x1, y1, x2, y2);

    gradient.addColorStop(0, '#A979F6');
    gradient.addColorStop(0.27, '#6C1BEC');
    gradient.addColorStop(0.61, '#ED5619');
    gradient.addColorStop(0.92, '#F5CF71');

    this.gradientCtx.fillStyle = gradient;
    this.gradientCtx.fillRect(x1, y1, x2, y2);

    this.tileColors = {};
    for (let col = 0; col < this.gridCols; col++) {
      for (let row = 0; row < this.gridRows; row++) {
        const coord = `${col},${row}`;
        const rectX = col * this.gridTileSize + this.pxRatio * col;
        const rectY = row * this.gridTileSize + this.pxRatio * row;

        const {data} = this.gradientCtx.getImageData(rectX, rectY, 1, 1);
        const [r, g, b] = data;

        this.tileColors[coord] = `${r}, ${g}, ${b}`;
      }
    }
  }

  setVars() {
    this.BREAKPOINT_MOBILE = 481;
    this.BREAKPOINT_DESKTOP = 960;
    this.IDLE_ANIMATION_TIMEOUT = 1000; // idle time after which to start animation

    this.animate = this.animate.bind(this);
    this.animationIntervals = {};
    this.animationTimeouts = {};
    this.isAnimating = false;
    this.isMobile = window.innerWidth <= this.BREAKPOINT_MOBILE;
    this.isTablet = window.innerWidth > this.BREAKPOINT_MOBILE && window.innerWidth < this.BREAKPOINT_DESKTOP;
    this.isDesktop = window.innerWidth >= this.BREAKPOINT_DESKTOP;
    this.isIntersectingViewport = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)') === true || window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
    this.tiles = {};

    this.pxRatio = window.devicePixelRatio;

    if (this.pxRatio > 1) {
      this.pxRatio = Math.round(this.pxRatio);
    } else if (this.pxRatio < 1) {
      this.pxRatio = 1;
    }

    this.ctx = this.$canvas?.getContext('2d');
    this.gradientCtx = this.$gradientCanvas?.getContext('2d', {willReadFrequently: true});

    // Pre-computed properties
    this.computedPerlinSets = JSON.parse(this.$dataPerlin.innerText);
    this.currentPerlinSet = Math.floor(Math.random() * (this.computedPerlinSets.length - 1));
  }

  /**
   * @param {Number} [timeout] - optional timeout (defaults to IDLE_ANIMATION_TIMEOUT)
   */
  startIdleAnimation( timeout = this.IDLE_ANIMATION_TIMEOUT ) {
    if (this.isAnimating) {
      return;
    }
    this.isAnimating = true;

    this.animationTimeouts.startAnimation = setTimeout( () => {
      this.animationIntervals.rotatePerlin = setInterval( () => {
        // Rotate pre-computed Perlin sets
        let perlinSet = this.currentPerlinSet + 1;
        if (perlinSet > this.computedPerlinSets.length - 1) {
          perlinSet = 0;
        }
        this.currentPerlinSet = perlinSet;
      }, 4500 );

      let threshold = 0.25;
      this.animationIntervals.setThreshold = setInterval( () => threshold = threshold === 0.25 ? 0.33 : 0.25, 3000 );

      let animationInterval = 0;
      this.animationIntervals.setAnimationInterval = setInterval( () => animationInterval = animationInterval === 500 ? 1500 : 500, 3000 );

      let drawDelay = 750;
      this.animationIntervals.setDrawDelay = setInterval( () => drawDelay = drawDelay === 250 ? 750 : 250, 3000 );

      this.animationIntervals.setTileOpacity = setInterval( () => {
        for (let col = 0; col < this.gridCols; col++) {
          for (let row = 0; row < this.gridRows; row++) {
            if (Math.random() > threshold) {
              const value = this.getPerlin(col, row);
              let opacity = value / this.computedPerlinSets[this.currentPerlinSet].max;

              if (opacity > 0) {
                let avoidanceScore = 0;
                this.avoidanceAreas.forEach( (avoidanceArea) => {
                  let inAvoidanceArea = true;
                  inAvoidanceArea = inAvoidanceArea && col >= avoidanceArea.col1 - 1;
                  inAvoidanceArea = inAvoidanceArea && col <= avoidanceArea.col2 + 1;
                  inAvoidanceArea = inAvoidanceArea && row >= avoidanceArea.row1 - 1;
                  inAvoidanceArea = inAvoidanceArea && row <= avoidanceArea.row2 + 1;

                  if (inAvoidanceArea) {
                    avoidanceScore = 0; // higher score = greater opacity

                    if (col - avoidanceArea.col1 < 3) {
                      avoidanceScore += 3 - (col - avoidanceArea.col1);
                    }
                    if (row - avoidanceArea.row1 < 3) {
                      avoidanceScore += 3 - (row - avoidanceArea.row1);
                    }
                    if (avoidanceArea.col2 - col < 3) {
                      avoidanceScore += 3 - (avoidanceArea.col2 - col);
                    }
                    if (avoidanceArea.row2 - row < 3) {
                      avoidanceScore += 3 - (avoidanceArea.row2 - row);
                    }

                    // Reduce opacityMax for tiles in an avoidance area
                    opacity = opacity < 0.10 ? opacity : 0 + avoidanceScore * 0.05;

                    if (this.isMobile) {
                      // Additional reduction for mobile
                      opacity = opacity < 0.10 ? opacity : 0.10;
                    }
                  }
                });

                this.drawAt(col, row, {
                  clearAfter: 750 + Math.random() * 1000,
                  delay: 500 + Math.random() * drawDelay,
                  opacityMax: opacity,
                });
              }
            }
          }
        }
      }, animationInterval );
    }, timeout );
  }

  /**
   * Cancel all animation timeouts and intervals, and override opacity direction
   * to start fading out.
   */
  stopIdleAnimation() {
    Object.keys(this.tiles).forEach( (coord) => {
      // Fade out animated tiles for a smoother transition
      this.tiles[coord].opacityDirection = -1;
    });

    Object.entries(this.animationIntervals).forEach( ([name, interval]) => {
      clearInterval( interval );
      delete this.animationIntervals[name];
    });

    Object.entries(this.animationTimeouts).forEach( ([name, timeout]) => {
      clearTimeout( timeout );
      delete this.animationTimeouts[name];
    });

    this.isAnimating = false;
  }

  /**
   * Calculate and set grid-based coordinates of areas to avoid when rendering
   * the idle animation.
   *
   * Called on initialization and whenever the window is resized.
   */
  updateAvoidanceAreas() {
    this.avoidanceAreas = [];
    this.$avoidanceElements.forEach( ($avoidanceElement) => {
      const {top, left, bottom, right} = $avoidanceElement.getBoundingClientRect();

      const {col: col1, row: row1} = this.getCoordinates( left, top );
      const {col: col2, row: row2} = this.getCoordinates( right, bottom );

      this.avoidanceAreas.push( {col1, row1, col2, row2} );
    });
  }

  isZooming() {
    const newValue = window.devicePixelRatio;
    if (newValue !== this.pxRatio) {
      const event = new Event('devicepixelratiochange');
      event.oldValue = this.pxRatio;
      event.newValue = newValue;
      this.pxRatio = Math.round(newValue);
      window.dispatchEvent(event);
    }
  }

  /**
   * Calculate and set the height and width of the canvas, setting the internal
   * grid tile size to match.
   *
   * Called on initialization and whenever the window is resized.
   *
   * @return {Promise}
   */
  updateCanvas() {
    return new Promise( (resolve) => {
      setTimeout( () => {
        const {height, width} = this.$element.getBoundingClientRect();

        this.$canvas.height = height * this.pxRatio;
        this.$canvas.width = width * this.pxRatio;
        this.$gradientCanvas.height = this.$canvas.height;
        this.$gradientCanvas.width = this.$canvas.width;

        if (this.isMobile) {
          this.gridCols = 14;
        } else if (this.isTablet) {
          this.gridCols = 22;
        } else if (this.isDesktop) {
          this.gridCols = 30;
        }

        this.gridTileSize = Math.ceil((this.$canvas.width - ((this.gridCols - 1) * this.pxRatio)) / this.gridCols);
        this.gridRows = Math.ceil(this.$canvas.height / this.gridTileSize);

        resolve();
      }, 50);
    });
  }
}

registerModule('HomepageHero', HomepageHero);
